import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type FeaturesService from 'teacher-ui-client/services/features'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type BookLibraryBookMetadata from 'teacher-ui-client/models/book-library/book-metadata'
import { isBlank } from '@ember/utils'

interface Args {
  book: BookLibraryBook
}

interface Signature {
  Args: Args
  Blocks: {
    default: [Pair[]]
  }
}

interface Pair {
  name: string
  value: string
}

type BookMetadataKey = keyof BookLibraryBookMetadata

export class BookLibraryBookDetailPairs extends Component<Signature> {
  @service intl!: IntlService
  @service features!: FeaturesService

  valueToString(value: unknown): string | undefined {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    if (Array.isArray(value)) {
      return value.join(', ')
    }
  }

  /**
   * Name value pairs for book metadata attributes to display on the right of the modal
   * Backend will return nullish value for any attributes that aren't to be shown for this teacher when the search was
   * performed.
   */
  get nameValuePairs() {
    const { book } = this.args
    if (!book) return []

    const maybeFpPeaks = this.features.isEnabled('tui_fp_teacher_library_filter') ? ['fpPeaks' as const] : []

    const allBookAttrs: BookMetadataKey[] = [
      // 'blakeReadingLevel', // reading level is left out, as it appears on the left of the modal
      'lexileLevel', // lexileLevel, rather than lexileRange (which is a range)
      ...maybeFpPeaks,
      'rrLevel',
      'fpAz',
      'readingAge',
      'yearLevel',
      'dra',
      'bookColour',
    ]

    return allBookAttrs.reduce((pairs: Pair[], bookAttr) => {
      const value = this.valueToString(book.bookMetadata[bookAttr])

      if (!isBlank(value)) {
        const prefix = `bookLibrary.bookAttr.metaData.${String(bookAttr)}`
        const nameLabel = this.intl.t(`${prefix}.name`)
        const valueLabel = this.intl.t(`${prefix}.value`, { value })

        return [...pairs, { name: nameLabel, value: valueLabel }]
      }

      return pairs
    }, [])
  }

  <template>{{yield this.nameValuePairs}}</template>
}

export default BookLibraryBookDetailPairs
